import { Base64 } from 'js-base64';
import { tagging } from './tagging';
import { dataAttributes, dataObj } from './getdata';
import { saveLeadFingerprint, usedToChat } from './localStorage';
import axios, { post } from 'axios';
import { adminId, isClosedConversationStore } from '../store';
import { checkIfSomeItemCloseConversation, getRefererWithExpiry } from './utilities';

let host;

/**
 * Sets the host URL for API requests.
 *
 * @param {string} h - The host URL to set.
 */
const setHost = (h) => {
  host = h;
};

/**
 * Gets the currently set host URL.
 *
 * @returns {string} The current host URL.
 */
const getHost = () => host;

// Log the exact outgoing URL plus readable copies of its encoded payloads.
const logRedirect = (url) => {
  const params = Object.fromEntries(new URL(url, window.location.href).searchParams);
  const decoded = {};
  for (const key of ['data', 'booking']) {
    if (params[key]) {
      try {
        decoded[key] = JSON.parse(Base64.decode(params[key]));
        if (key === 'booking' && decoded[key]?.data) {
          decoded[key].data = JSON.parse(Base64.decode(decoded[key].data));
        }
      } catch (_) {
        decoded[key] = params[key];
      }
    }
  }
  console.log('[MY_HOST] Antes de abrir la ventana', { url, params, decoded });
};

/**
 * Encodes event data into a Base64 string.
 *
 * @param {Object} data - The event data to encode.
 * @returns {string} The Base64 encoded data.
 */
const encodeEventData = (data) => {
  return Base64.encodeURI(JSON.stringify(data));
};

/**
 * Constructs a parameter object and query string based on provided details.
 *
 * @param {string} inputData - The encoded data to include.
 * @param {string} [buyUrl=null] - The optional buy URL.
 * @param {obj} [booking=null] - The optional buy URL.
 * @returns {{paramObj: Object, extra: string}} The constructed parameter object and query string.
 */
const getLandingAiId = () => {
  const s = document.querySelector('script[data-landing-ai]');
  const id = s?.getAttribute('data-landing-ai');
  return id && id !== '[LANDING_ID]' ? id : null;
};

const constructParams = (inputData, buyUrl = null, booking = null, reSendEmail = null, reSendWhatsapp = null) => {
  const paramObj = {
    data: inputData,
    screenwidth: window.screen.width,
    url: window.location.href,
  };

  if (booking) {
    paramObj['booking'] = booking;
  }

  if (reSendEmail !== null) {
    paramObj.reSendEmail = reSendEmail;
  }

  if (reSendWhatsapp !== null) {
    paramObj.reSendWhatsapp = reSendWhatsapp;
  }

  let extra = `&url=${encodeURIComponent(window.location.href)}`;

  ['product', 'price', 'fullMessage'].forEach(attr => {
    if (dataAttributes[attr]) {
      extra += `&${attr}=${encodeURIComponent(dataAttributes[attr])}`;
      paramObj[attr] = encodeURIComponent(dataAttributes[attr]);
    }
  });

  if (buyUrl) {
    extra += `&buyUrl=${encodeURIComponent(buyUrl)}`;
    paramObj.buyUrl = encodeURIComponent(buyUrl);
  }

  const landingAiId = getLandingAiId();
  if (landingAiId) {
    extra += `&landingAi=${encodeURIComponent(landingAiId)}`;
    paramObj.landingAi = landingAiId;
  }

  return { paramObj, extra };
};

/**
 * Sends data via API or redirects the user based on specific conditions.
 *
 * @param {Object} person - The person object containing the recipient's details.
 * @param {string} [redirectUrl] - The optional URL to redirect after sending the form.
 * @param {string} [inputData=''] - The encoded data to send.
 * @param {string} [buyUrl=null] - The URL to be included as a buy link.
 * @param {boolean} [notQualified=false] - Flag to indicate if the person is not qualified for redirection.
 */
const send = async (person, redirectUrl, inputData = '', buyUrl = null, notQualified = false, isClosedConversation = false, extraData = null, businessId = null, threadId = null, reSendEmail = null, reSendWhatsapp = null) => {
  if (!isClosedConversation) tagging(person, extraData);
  usedToChat();
  saveLeadFingerprint();

  // Inject context phone for Inbox Mode (to ensure client matching)
  if (dataObj && dataObj.inboxPhone && inputData && typeof inputData === 'string') {
    try {
      let dataArr = JSON.parse(atob(inputData.replace(/-/g, '+').replace(/_/g, '/')));
      if (Array.isArray(dataArr)) {
        const hasTel = dataArr.some(i => i.type === 'tel');
        if (!hasTel) {
          dataArr.push({
            label: "Phone",
            val: dataObj.inboxPhone,
            type: "tel"
          });
          inputData = btoa(JSON.stringify(dataArr)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        }
      }
    } catch (e) {
      console.error("Error injecting inbox phone", e);
    }
  }

  // ADMIN / INBOX MODE CHECK
  if (dataObj && (dataObj.inboxToken || dataObj.adminCreateLead)) {
    try {
      const { paramObj } = constructParams(inputData, buyUrl, null, reSendEmail, reSendWhatsapp);
      const requestConfig = dataObj.inboxToken
        ? { headers: { 'INBOX-SERVICES-TOKEN': dataObj.inboxToken } }
        : undefined;
      let response;

      if (dataObj.editModeStatId) {
        // Lead existente: el backend lee reSendEmail desde el cuerpo JSON del POST.
        const apiUrl = `${host}/api/business/send/${person.id}?formEditMode=1&statsId=${dataObj.editModeStatId}`;
        response = await axios.post(apiUrl, paramObj, requestConfig);
      } else {
        // Lead nuevo: POST para evitar el redirect a WhatsApp que haría el GET.
        // formEditMode requires a valid Inbox token; without one, a normal POST still
        // creates the lead and keeps the administrator inside the popup.
        const editModeParam = dataObj.inboxToken ? '?formEditMode=1' : '';
        const apiUrl = `${host}/api/business/send/${person.id}${editModeParam}`;
        response = await axios.post(apiUrl, paramObj, requestConfig);
      }

      if (typeof window !== 'undefined' && window.parent) {
        const eventType = dataObj.editModeStatId ? 'lead-updated' : 'lead-created';
        const statId = dataObj.editModeStatId || response?.data?.statId;
        const whatsappUrl = response?.data?.whatsappUrl ?? null;
        window.parent.postMessage({ type: eventType, statId, whatsappUrl }, '*');
      }
      return { status: 'ok' };
    } catch (e) {
      console.error("Error saving lead in inbox mode", e);
      alert("Error saving: " + e.message);
      return { error: e };
    }
  }

  const { paramObj, extra } = constructParams(inputData, buyUrl);
  const apiUrl = `${host}/api/business/send/${person.id}`;
  const screenWidthParam = `&screenwidth=${window.screen.width}`;

  try {
    if (!notQualified && !person.noWhatsappRedirect && !isClosedConversation) {
      let fullRedirectUrl = `${apiUrl}?data=${inputData}${extra}${screenWidthParam}`;
      if (businessId) {
        const originUrl = getRefererWithExpiry(businessId);
        if (originUrl) {
          fullRedirectUrl += `&originalReferer=${encodeURIComponent(originUrl)}`;
        }
      }
      if (threadId) {
        fullRedirectUrl += `&thread=${encodeURIComponent(threadId)}`;
      }
      logRedirect(fullRedirectUrl);
      window.open(fullRedirectUrl, '_blank');
    } else if (redirectUrl && !isClosedConversation) {
      window.open(redirectUrl);
    } else {
      if (businessId) {
        const originUrl = getRefererWithExpiry(businessId);
        if (originUrl) {
          paramObj.originalReferer = originUrl;
        }
      }
      if (threadId) {
        paramObj.thread = threadId;
      }
      const response = await axios.post(apiUrl, paramObj);
      if (dataObj && dataObj.inboxPhone && !dataObj.inboxToken && typeof window !== 'undefined' && window.parent) {
        window.parent.postMessage({ type: 'lead-created', phone: dataObj.inboxPhone }, '*');
      }
      return response.data;
    }
  } catch (error) {
    console.error('Error sending data:', error);
  }
};

/**
 * Sends form data to a specified person.
 *
 * @param {Object} person - The person object containing the recipient's details.
 * @param {Object} event - The event object containing the form data and other event details.
 * @param {string} [redirectUrl] - The optional URL to redirect after sending the form.
 */
const sendform = async (person, event, redirectUrl, forceNoRedirect = false, businessId, threadId = null) => {
  let closeConversation = checkIfSomeItemCloseConversation(event.detail.data) || forceNoRedirect;
  let extraData = getExtraData(event.detail.data);
  let cleanData = cleanSelectorsData(event.detail.data);
  const encodedData = encodeEventData(cleanData);
  const response = await send(person, redirectUrl, encodedData, event.detail?.url, event.detail.notQualified, closeConversation, extraData, businessId, threadId, event.detail?.reSendEmail ?? null, event.detail?.reSendWhatsapp ?? null);
  return response?.lead ?? null;
};

/**
 * Uploads a file to the server for the specified WhatsApp ID.
 *
 * @param {File} file - The file to upload.
 * @param {string} whatsappId - The WhatsApp ID associated with the file.
 * @returns {Promise} A promise resolving the server response.
 */
const fileUpload = async (file, whatsappId) => {
  const url = `${host}/api/user-file?whatsappId=${whatsappId}`;
  const formData = new FormData();
  formData.append('file', file);

  const config = {
    headers: { 'content-type': 'multipart/form-data' },
  };

  try {
    return await post(url, formData, config);
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * Retrieves the booking status for a specific booking configuration and time range.
 *
 * @param {string} bookingConfigId - The ID of the booking configuration.
 * @param {string} startTime - The start time for the booking.
 * @param {string} endTime - The end time for the booking.
 * @returns {Promise} A promise resolving the server response.
 */
const getBookingStatus = async (bookingConfigId, startTime, endTime, quantity, arrivalTime) => {
  const url = `${host}/api/booking/${bookingConfigId}/status`;

  try {
    return await post(url, {
      startTime,
      endTime,
      ...(quantity !== undefined ? { quantity } : {}),
      ...(arrivalTime !== undefined ? { arrivalTime } : {}),
    });
  } catch (error) {
    console.error('Error fetching booking status:', error);
    throw error;
  }
};

/**
 * Sends booking data to a specified person, handling different device and redirection logic.
 *
 * @param {Object} person - The person object containing the recipient's details.
 * @param {Object} event - The event object containing the booking data and other event details.
 */
const sendBooking = async (person, event) => {
  let isClosedConversation = checkIfSomeItemCloseConversation(event.detail.data);

  if (!isClosedConversation) tagging(person);

  const cleanData = cleanSelectorsData(event.detail.data);
  const encodedData = encodeEventData(cleanData);
  const { paramObj, extra } = constructParams(encodedData, event.detail?.url, event.detail?.booking);
  const apiUrl = `${host}/api/business/send/${person.id}`;
  const screenWidthParam = `&screenwidth=${window.screen.width}`;
  const bookingParam = `&booking=${Base64.encodeURI(JSON.stringify({ data: encodedData, booking: event.detail.booking, url: encodeURIComponent(window.location.href) }))}`;

  let termsParam = '';
  if (event.detail?.termsAccepted !== undefined) {
    paramObj.termsAccepted = event.detail.termsAccepted ? 1 : 0;
    termsParam = `&termsAccepted=${paramObj.termsAccepted}`;
  }

  try {
    if (person.noWhatsappRedirect || isClosedConversation) {
      await axios.post(apiUrl, paramObj);
    } else if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      // Handle iOS-specific logic
      const a = document.createElement("a");
      document.body.appendChild(a);
      a.style.display = "none";
      a.href = `${apiUrl}?data=${encodedData}${bookingParam}${extra}${screenWidthParam}${termsParam}`;
      logRedirect(a.href);
      a.click();
      document.body.removeChild(a);
    } else {
      // Handle non-iOS logic
      const fullRedirectUrl = `${apiUrl}?data=${encodedData}${bookingParam}${extra}${screenWidthParam}${termsParam}`;
      logRedirect(fullRedirectUrl);
      window.open(fullRedirectUrl);
    }
  } catch (error) {
    console.error('Error sending booking data:', error);
  }
};

/**
 * Checks if a person has form data available.
 *
 * @param {Object} person - The person object containing the form data.
 * @returns {boolean} True if the person has form data, false otherwise.
 */
const personHasFormData = (person) => {
  return person.form != null && typeof person.form === 'object' && Object.keys(person.form).length > 0 && person.form.constructor === Object;
};

/**
 * Retrieves the extra data to include in the API request.
 *
 * @param {Object} data - The form data to include.
 */
const getExtraData = (data) => {
  let extraData = {};
  data.forEach(item => {
    if (item.type === "text" && item.subtype === "firstname") {
      extraData.firstname = item.val;
    }
    if (item.type === "email") {
      extraData.email = item.val;
    }
    if (item.type === "tel") {
      extraData.tel = item.val;
    }
  });
  return extraData;
};


/**
 * Clean the data of the selectors
 * @param {Array} items - The form data to clean
 */
const cleanSelectorsData = (items) => {
  return items.map(item => {
    if (item.dataObject) {
      return {
        ...item,
        dataObject: item.dataObject.filter(opcion =>
          item.val === opcion.value
        )
      };
    }
    return { ...item };
  });
};

export {
  personHasFormData,
  sendform,
  send,
  setHost,
  getHost,
  sendBooking,
  fileUpload,
  getBookingStatus,
  cleanSelectorsData
};
